import { useState, useEffect, useRef } from 'react';
import { CreditCard, Wallet, Smartphone, Zap, Loader2, CheckCircle, X, AlertCircle } from 'lucide-react';
import paymentController from '../controllers/paymentController';
import { authController } from '../controllers/authController';

// Har gateway ka generic icon + naam (koi trademarked logo use nahi kiya)
const GATEWAY_META = {
  razorpay: { icon: CreditCard, label: 'Razorpay',  desc: 'Card, UPI, Netbanking, Wallet, EMI' },
  cashfree: { icon: Wallet,     label: 'Cashfree',  desc: 'Card, UPI, Netbanking, Wallet' },
  paytm:    { icon: Smartphone, label: 'Paytm',     desc: 'Card, UPI, Netbanking, Paytm Wallet' },
  phonepe:  { icon: Zap,        label: 'PhonePe',   desc: 'UPI, Card, Netbanking' },
};

const PaymentGatewayModal = ({ isOpen, onClose, planNames, amount, billingCycle, notes, onPaymentSuccess, }) => {
  const [gateways, setGateways]     = useState([]);
  const [loadingGw, setLoadingGw]   = useState(true);
  const [selected, setSelected]     = useState(null);
  const [status, setStatus]         = useState('idle'); // idle | creating | processing | polling | success | failed
  const [error, setError]           = useState('');
  const pollRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setStatus('idle'); setError(''); setSelected(null);
    (async () => {
      setLoadingGw(true);
      const res = await paymentController.getActiveGateways();
      setGateways(res.data || []);
      if (res.data?.length === 1) setSelected(res.data[0].id); // ek hi active ho to auto-select
      setLoadingGw(false);
    })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [isOpen]);

  const user = authController.getCurrentUser();

  const finishSuccess = () => {
    setStatus('success');
    setTimeout(() => { onPaymentSuccess?.(); }, 1200);
  };

  const finishFailure = (msg) => {
    setError(msg || 'Payment fail ho gaya. Dobara try karein.');
    setStatus('failed');
  };

  // Helper — notes.sectionIds (comma-separated string) ko planIds array mein todta hai.
  // Backend (razorpayController/cashfreeController) ab isi array se Invoice ke
  // andar plan naam/pricing resolve karta hai, isliye ye hamesha bhejna zaroori hai.
  const resolvePlanIds = () => {
    if (!notes?.sectionIds) return [];
    return String(notes.sectionIds).split(',').map(s => s.trim()).filter(Boolean);
  };

  // ── Poll-based verify — Paytm/PhonePe/Cashfree ke redirect flow ke liye ────
  // invoiceId ab parameter ke through pass hota hai, kyunki yeh function
  // handlePay ke andar wale invoiceId variable ko closure se access nahi kar sakta.
  const startPolling = (checkFn, successCheck, invoiceId, { maxTries = 40, intervalMs = 3000, failCheck = null, failMsg = null } = {}) => {
    setStatus('polling');
    let tries = 0;
    pollRef.current = setInterval(async () => {
      tries++;
      let res;
      try {
        res = await checkFn();
      } catch (err) {
        console.error('Polling check failed:', err);
        if (tries >= maxTries) {
          clearInterval(pollRef.current);
          finishFailure('Payment confirmation timeout ho gaya. Agar paisa kata hai to thodi der baad refresh karein.');
        }
        return;
      }
      if (res.success && successCheck(res.data)) {
        clearInterval(pollRef.current);
        setStatus('success');
        setTimeout(() => onPaymentSuccess?.(invoiceId), 1200);
      } else if (res.success && failCheck && failCheck(res.data)) {
        clearInterval(pollRef.current);
        finishFailure(failMsg || 'Payment cancel/expire ho gaya. Dobara try karein.');
      } else if (tries >= maxTries) {
        clearInterval(pollRef.current);
        finishFailure('Payment confirmation timeout ho gaya. Agar paisa kata hai to thodi der baad refresh karein.');
      }
    }, intervalMs);
  };

  const handlePay = async () => {
    if (!selected) { setError('Pehle ek payment method select karein'); return; }
    setError(''); setStatus('creating');

    const receipt = `RCPT-${Date.now()}`;
    const planIds = resolvePlanIds();
    let invoiceId = null;

    const finishSuccessWithInvoice = () => {
      setStatus('success');
      setTimeout(() => onPaymentSuccess?.(invoiceId), 1200);
    };

    try {
      if (selected === 'razorpay') {
        const orderRes = await paymentController.razorpay.createOrder({
          amount, receipt, notes, planIds, billingCycle,
        });
        if (!orderRes.success) return finishFailure(orderRes.message);
        invoiceId = orderRes.data?.invoiceId || null;

        setStatus('processing');
        await paymentController.razorpay.checkout(orderRes.data, user,
          async (response) => {
            const verifyRes = await paymentController.razorpay.verify({
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
              invoiceId,
            });
            verifyRes.success ? finishSuccessWithInvoice() : finishFailure(verifyRes.message);
          },
          (msg) => finishFailure(msg)
        );
      }

      else if (selected === 'cashfree') {
        const orderRes = await paymentController.cashfree.createOrder({
          amount,
          customer: {
            id: user?.id,
            name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
            email: user?.email,
            phone: user?.phone,
          },
          meta: notes,
          planIds,
          billingCycle,
        });
        if (!orderRes.success) return finishFailure(orderRes.message);
        invoiceId = orderRes.data?.invoiceId || null;

        // Backend snake_case (order_id) return karta hai, camelCase (orderId) nahi —
        // dono check karo taaki kisi bhi shape mein order id mil jaaye
        const cfOrderId = orderRes.data?.orderId || orderRes.data?.order_id;

        if (!cfOrderId) {
          console.error('Cashfree order id missing in createOrder response:', orderRes.data);
          return finishFailure('Order ID nahi mila — payment shuru nahi ho paya.');
        }

        await paymentController.cashfree.checkout(orderRes.data, (msg) => finishFailure(msg));

        // fetchOrder ka response kabhi 2-layer {success,data:order} hota hai,
        // kabhi apiService ka debug wrapper extra layer add kar deta hai —
        // ye normalizer chahe jitni layers ho, order object dhoondh ke nikal leta hai
        const normalizeCfOrder = (raw) => {
          const inner   = raw?.data?.data ?? raw?.data ?? raw;
          const success = raw?.success ?? raw?.data?.success ?? true;
          return { success, data: inner };
        };

        // 🔧 FIX: backend fetchOrder ab invoiceStatus bhi return karta hai
        // (invoice ko DB mein khud paid mark kar deta hai jab Cashfree order
        // PAID ho). Ab dono check kar rahe hain — Cashfree ka raw
        // order_status AUR humara invoiceStatus — taaki koi bhi edge case
        // (jaise field naming mismatch) miss na ho aur asli DB state
        // reliably reflect ho.
        startPolling(
          async () => {
            const raw = await paymentController.cashfree.fetchOrder(cfOrderId);
            const normalized = normalizeCfOrder(raw);
            console.log(
              'Cashfree poll — order_status:', normalized.data?.order_status,
              '| invoiceStatus:', normalized.data?.invoiceStatus
            );
            return normalized;
          },
          (data) => data?.order_status === 'PAID' || data?.invoiceStatus === 'paid',
          invoiceId,
          {
            maxTries: 20,
            intervalMs: 3000,
            failCheck: (data) => ['EXPIRED', 'TERMINATED', 'VOID', 'CANCELLED'].includes(data?.order_status),
            failMsg: 'Payment cancel/expire ho gaya. Dobara try karein.',
          }
        );
      }

      else if (selected === 'paytm') {
        // ⚠️ Note: paytmController.js abhi Invoice model se wired nahi hai —
        // invoiceId yahan null hi rahega jab tak wo controller update na ho
        const orderRes = await paymentController.paytm.createOrder(amount, user?.id, user?.email, user?.phone);
        if (!orderRes.success) return finishFailure(orderRes.message);
        invoiceId = orderRes.data?.invoiceId || null;

        paymentController.paytm.checkout(orderRes.data);
        startPolling(
          () => paymentController.paytm.verify(orderRes.data.params?.ORDER_ID || receipt),
          (data) => data?.status === 'TXN_SUCCESS' || data?.txnStatus === 'SUCCESS',
          invoiceId,
          {
            failCheck: (data) => data?.status === 'TXN_FAILURE' || data?.txnStatus === 'FAILURE',
            failMsg: 'Payment fail ho gaya. Dobara try karein.',
          }
        );
      }

      else if (selected === 'phonepe') {
        // ⚠️ Note: phonepeController.js bhi abhi Invoice model se wired nahi hai
        const orderRes = await paymentController.phonepe.createOrder(amount, user?.id, user?.phone);
        if (!orderRes.success) return finishFailure(orderRes.message);
        invoiceId = orderRes.data?.invoiceId || null;

        paymentController.phonepe.checkout(orderRes.data);
        startPolling(
          () => paymentController.phonepe.checkStatus(orderRes.data.merchantTransactionId || receipt),
          (data) => data?.status === 'SUCCESS' || data?.code === 'PAYMENT_SUCCESS',
          invoiceId,
          {
            failCheck: (data) => data?.status === 'FAILED' || data?.code === 'PAYMENT_ERROR',
            failMsg: 'Payment fail ho gaya. Dobara try karein.',
          }
        );
      }
    } catch (err) {
      finishFailure(err.message || 'Kuch galat ho gaya');
    }
  };

  if (!isOpen) return null;

  const s = {
    overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:3000, padding:16 },
    card: { background:'#fff', borderRadius:16, width:'100%', maxWidth:440, padding:24, boxShadow:'0 20px 60px rgba(0,0,0,0.25)', position:'relative' },
    close: { position:'absolute', top:14, right:14, background:'transparent', border:'none', cursor:'pointer', color:'#94a3b8' },
    title: { fontSize:18, fontWeight:700, color:'#1e293b', margin:'0 0 4px' },
    sub: { fontSize:13, color:'#64748b', marginBottom:16 },
    summaryBox: { background:'#f8fafc', borderRadius:10, padding:14, marginBottom:18 },
    planRow: { display:'flex', justifyContent:'space-between', fontSize:13, color:'#475569', padding:'3px 0' },
    totalRow: { display:'flex', justifyContent:'space-between', fontSize:16, fontWeight:700, color:'#1e293b', paddingTop:10, marginTop:6, borderTop:'1px solid #e2e8f0' },
    gwLabel: { fontSize:12, fontWeight:700, color:'#475569', textTransform:'uppercase', letterSpacing:'0.4px', marginBottom:10 },
    gwOption: (active) => ({
      display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10,
      border: active ? '2px solid #3b82f6' : '1px solid #e2e8f0',
      background: active ? '#eff6ff' : '#fff',
      cursor:'pointer', marginBottom:8, transition:'all .15s',
    }),
    gwIconWrap: (active) => ({
      width:36, height:36, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center',
      background: active ? '#dbeafe' : '#f1f5f9', color: active ? '#2563eb' : '#64748b', flexShrink:0,
    }),
    gwName: { fontSize:13, fontWeight:700, color:'#1e293b' },
    gwDesc: { fontSize:11, color:'#94a3b8', marginTop:1 },
    payBtn: {
      width:'100%', padding:13, borderRadius:9, border:'none', marginTop:16,
      background: (!selected || status==='creating' || status==='processing' || status==='polling') ? '#94a3b8' : '#2563eb',
      color:'#fff', fontWeight:700, fontSize:14,
      cursor: (!selected || status==='creating' || status==='processing' || status==='polling') ? 'not-allowed' : 'pointer',
      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
    },
    errorBox: { background:'#fef2f2', color:'#dc2626', padding:'10px 12px', borderRadius:8, fontSize:12, marginTop:12, display:'flex', alignItems:'center', gap:8 },
    successWrap: { textAlign:'center', padding:'20px 0' },
    pollingWrap: { textAlign:'center', padding:'20px 0', color:'#64748b', fontSize:13 },
  };

  return (
    <div style={s.overlay} onClick={status === 'idle' ? onClose : undefined}>
      <div style={s.card} onClick={e => e.stopPropagation()}>
        {status !== 'processing' && status !== 'polling' && (
          <button style={s.close} onClick={onClose}><X size={18} /></button>
        )}

        {status === 'success' ? (
          <div style={s.successWrap}>
            <CheckCircle size={52} color="#10b981" style={{ marginBottom:12 }} />
            <h3 style={{ margin:'0 0 6px', color:'#1e293b' }}>Payment Successful!</h3>
            <p style={{ fontSize:13, color:'#64748b' }}>Subscription activate ho gayi hai. Invoice email pe bhej diya gaya hai.</p>
          </div>
        ) : status === 'polling' ? (
          <div style={s.pollingWrap}>
            <Loader2 size={36} color="#3b82f6" style={{ animation:'spin 1s linear infinite', marginBottom:12 }} />
            <p>Payment confirm hone ka wait ho raha hai…</p>
            <p style={{ fontSize:11, color:'#94a3b8' }}>Naye tab mein payment complete karne ke baad yahan wapas aa jayein</p>
          </div>
        ) : (
          <>
            <h3 style={s.title}>Complete Payment</h3>
            <p style={s.sub}>Billing: {billingCycle}</p>

            <div style={s.summaryBox}>
              {planNames.map((name, i) => (
                <div key={i} style={s.planRow}><span>{name}</span></div>
              ))}
              <div style={s.totalRow}>
                <span>Total</span>
                <span>₹{amount}</span>
              </div>
            </div>

            <div style={s.gwLabel}>Select Payment Method</div>

            {loadingGw ? (
              <div style={{ textAlign:'center', padding:20, color:'#94a3b8' }}>
                <Loader2 size={20} style={{ animation:'spin 1s linear infinite' }} />
              </div>
            ) : gateways.length === 0 ? (
              <div style={s.errorBox}><AlertCircle size={14} /> Koi payment gateway configured nahi hai. Admin se contact karein.</div>
            ) : (
              gateways.map(g => {
                const meta = GATEWAY_META[g.id] || { icon: CreditCard, label: g.name, desc: '' };
                const Icon = meta.icon;
                const active = selected === g.id;
                return (
                  <div key={g.id} style={s.gwOption(active)} onClick={() => setSelected(g.id)}>
                    <div style={s.gwIconWrap(active)}><Icon size={18} /></div>
                    <div>
                      <div style={s.gwName}>{meta.label}</div>
                      <div style={s.gwDesc}>{meta.desc}</div>
                    </div>
                  </div>
                );
              })
            )}

            {error && <div style={s.errorBox}><AlertCircle size={14} /> {error}</div>}

            <button style={s.payBtn} onClick={handlePay} disabled={!selected || status === 'creating' || status === 'processing'}>
              {(status === 'creating' || status === 'processing') ? (
                <><Loader2 size={16} style={{ animation:'spin 1s linear infinite' }} /> Processing…</>
              ) : (
                `Pay ₹${amount}`
              )}
            </button>
          </>
        )}
      </div>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
};

export default PaymentGatewayModal;